import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Button,
  Label,
  PageContainer,
  PageIntroCard,
  PageSectionCard,
  Skeleton,
  toast,
} from '@kmgestao/ui';

import {
  formatCnpj,
  formatPhoneBR,
  hasStageAction,
  isTerminalStage,
  LEAD_ACTIVITY_TYPES,
  LEAD_SOURCE_LABELS,
  LOST_REASON_LABELS,
  SPECIALTY_LABELS,
  useLatestSignedContract,
  useLead,
  useLeadActivities,
  useProvisionedClinicForLead,
  useUpdateLeadNotes,
  type LeadActivityType,
  type LeadAddress,
} from '../../../features/leads/index.js';
import { SuperadminShell } from '../SuperadminShell.js';

import { DeleteLeadDialog } from './DeleteLeadDialog.js';
import { EditLeadDialog } from './EditLeadDialog.js';
import { LeadCustomerBanner } from './LeadCustomerBanner.js';
import { LeadFormSubmissionsSection } from './LeadFormSubmissionsSection.js';
import { LeadProposalsSection } from './LeadProposalsSection.js';
import { LeadStageBadge } from './LeadStageBadge.js';
import { MarkAsLostDialog } from './MarkAsLostDialog.js';
import { NewProposalDialog } from './NewProposalDialog.js';
import { SendQuestionnaireButton } from './SendQuestionnaireButton.js';

const ACTIVITY_LABELS: Record<LeadActivityType, string> = {
  note: 'Nota',
  email_sent: 'Email enviado',
  email_received: 'Email recebido',
  call: 'Ligação',
  meeting: 'Reunião',
  form_sent: 'Questionário enviado',
  form_viewed: 'Questionário visualizado',
  form_submitted: 'Questionário respondido',
  proposal_sent: 'Proposta enviada',
  proposal_viewed: 'Proposta visualizada',
  proposal_accepted: 'Proposta aceita',
  proposal_rejected: 'Proposta recusada',
  contract_sent: 'Contrato enviado',
  contract_viewed_by_lead: '👀 Contrato visualizado pelo lead',
  contract_approved_by_lead: '✅ Contrato aprovado pelo lead',
  contract_adjustment_requested: '✏️ Lead solicitou ajuste no contrato',
  contract_signed: '✍️ Contrato assinado',
  provisioning_started: 'Provisionamento iniciado',
  invite_sent: '📨 Convite de ativação enviado',
  invite_resent: '📨 Convite reenviado',
  invite_accepted: '🎉 Cliente ativou o acesso',
  provisioning_completed: 'Provisionamento concluído',
  provisioning_failed: 'Provisionamento falhou',
  stage_changed: 'Estado alterado',
  score_updated: 'Score atualizado',
};

/**
 * Detalhe do lead — seções:
 *   1. Intro (identificação + dados cadastrais + badge de estado)
 *   2. Ações (botões disparando edge functions do funil)
 *   3. Timeline (lead_activities cronológicas reversas)
 *   4. Notas internas (editáveis com save)
 *
 * "Enviar questionário" fica desabilitado até M1-01b (template seedado).
 * "Criar proposta" até M1-01c. Outros fluxos do funil virão nos PRs
 * seguintes — o detalhe aqui já prevê a superfície.
 */
export function LeadDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const lead = useLead(id);
  const activities = useLeadActivities(id);
  const updateNotes = useUpdateLeadNotes();
  // Contrato signed serve de âncora pro botão "Provisionar agora" do
  // LeadCustomerBanner. Lazy: query só fica enabled quando há lead id.
  const signedContract = useLatestSignedContract(id);
  // Clínica provisionada serve de âncora pro botão "Acessar painel"
  // — só faz sentido quando lead.stage === 'provisioned'.
  const provisionedClinic = useProvisionedClinicForLead(id);

  const [notes, setNotes] = useState('');
  const [notesDirty, setNotesDirty] = useState(false);
  const [lostOpen, setLostOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  // Sincroniza notas do servidor quando o lead é carregado ou atualizado
  // externamente (ex: outro admin editou). `notesDirty` garante que
  // não sobrescrevemos input do usuário enquanto ele digita.
  useEffect(() => {
    if (lead.data && !notesDirty) {
      setNotes(lead.data.notes ?? '');
    }
  }, [lead.data, notesDirty]);

  const onSaveNotes = async () => {
    if (!lead.data) return;
    await updateNotes.mutateAsync(
      { id: lead.data.id, notes: notes.trim() || null },
      {
        onSuccess: () => {
          toast.success('Notas salvas.');
          setNotesDirty(false);
        },
        onError: (err) => toast.error('Falha ao salvar', { description: (err as Error).message }),
      },
    );
  };

  if (lead.isLoading) {
    return (
      <SuperadminShell>
        <PageContainer>
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-48 w-full" />
        </PageContainer>
      </SuperadminShell>
    );
  }

  if (lead.isError || !lead.data) {
    return (
      <SuperadminShell>
        <PageContainer>
          <PageIntroCard
            eyebrow={
              <Link to="/superadmin/leads" className="hover:underline">
                ← Todos os leads
              </Link>
            }
            title="Lead não encontrado"
            description={
              lead.error?.message ?? 'O lead pode ter sido removido ou o link está incorreto.'
            }
          />
        </PageContainer>
      </SuperadminShell>
    );
  }

  const l = lead.data;
  const specialtyKey = l.specialty as keyof typeof SPECIALTY_LABELS | null;
  const specialtyLabel = specialtyKey ? (SPECIALTY_LABELS[specialtyKey] ?? l.specialty) : '—';
  const sourceKey = l.source as keyof typeof LEAD_SOURCE_LABELS | null;
  const sourceLabel = sourceKey ? (LEAD_SOURCE_LABELS[sourceKey] ?? l.source) : '—';
  const isArchived = l.stage === 'lost' || l.stage === 'provisioned';

  // Estado terminal = lead virou cliente. Página muda de tom:
  //   - Banner "Cliente desde X" assume protagonismo (substitui PageIntroCard)
  //   - Cards de funil ficam collapsed por default (consultáveis, não trabalháveis)
  //   - Ações de funil somem (criar proposta, reenviar questionário, marcar perdido)
  // PR-M1-01c-iii.b — 2026-04-25.
  const isCustomer = isTerminalStage(l.stage);

  return (
    <SuperadminShell>
      <PageContainer>
        {isCustomer ? (
          <div className="space-y-2">
            <Link
              to="/superadmin/leads"
              className="inline-flex text-xs text-muted-foreground hover:underline"
            >
              ← Todos os leads
            </Link>
            <LeadCustomerBanner
              lead={l}
              signedContractId={signedContract.data?.contract_id ?? null}
              signedProvisionPlan={signedContract.data?.provision_plan ?? null}
              signedBillingCycle={signedContract.data?.billing_cycle ?? null}
              clinicId={provisionedClinic.data?.clinic_id ?? null}
              ownerMemberStatus={provisionedClinic.data?.owner_member_status ?? null}
              subscriptionStatus={provisionedClinic.data?.subscription_status ?? null}
            />
          </div>
        ) : (
          <PageIntroCard
            eyebrow={
              <Link to="/superadmin/leads" className="hover:underline">
                ← Todos os leads
              </Link>
            }
            title={l.full_name}
            description={l.clinic_name_guess ?? 'Clínica ainda sem nome cadastrado.'}
            meta={<LeadStageBadge stage={l.stage} />}
          >
            <dl className="grid gap-x-6 gap-y-3 pt-3 text-sm sm:grid-cols-2">
              <div>
                <dt className="text-muted-foreground">Email</dt>
                <dd className="font-medium">{l.email}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Telefone</dt>
                <dd className="font-medium">{l.phone ? formatPhoneBR(l.phone) : '—'}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">CNPJ</dt>
                <dd className="font-mono text-xs">{l.cnpj ? formatCnpj(l.cnpj) : '—'}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Especialidade</dt>
                <dd className="font-medium">{specialtyLabel}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Origem</dt>
                <dd className="font-medium">{sourceLabel}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Criado em</dt>
                <dd className="font-medium">
                  {new Date(l.created_at).toLocaleString('pt-BR', { dateStyle: 'short' })}
                </dd>
              </div>
            </dl>
            {l.stage === 'lost' && l.lost_reason ? (
              <p className="mt-3 text-sm text-destructive">
                Motivo da perda:{' '}
                {LOST_REASON_LABELS[l.lost_reason as keyof typeof LOST_REASON_LABELS] ??
                  l.lost_reason}
              </p>
            ) : null}
          </PageIntroCard>
        )}

        <PageSectionCard
          title="Dados cadastrais e endereço"
          description={
            'Esses campos alimentam propostas e contratos. Conferir antes de ' +
            'gerar proposta, especialmente razão social e CEP.'
          }
          action={
            <Button size="sm" variant="outline" onClick={() => setEditOpen(true)}>
              Editar dados
            </Button>
          }
        >
          <dl className="grid gap-x-6 gap-y-3 text-sm sm:grid-cols-2">
            <div className="sm:col-span-2">
              <dt className="text-muted-foreground">Razão social</dt>
              <dd className="font-medium">{l.legal_name ?? '—'}</dd>
            </div>
            <div className="sm:col-span-2">
              <dt className="text-muted-foreground">Nome fantasia</dt>
              <dd className="font-medium">{l.clinic_name_guess ?? '—'}</dd>
            </div>
            <LeadAddressField label="CEP" value={formatAddressField(l.address, 'cep')} />
            <LeadAddressField label="Endereço" value={formatStreetLine(l.address)} wide />
            <LeadAddressField label="Bairro" value={formatAddressField(l.address, 'district')} />
            <LeadAddressField label="Cidade / UF" value={formatCityUf(l.address)} />
            <LeadAddressField
              label="Complemento"
              value={formatAddressField(l.address, 'complement')}
              wide
            />
          </dl>
        </PageSectionCard>

        <PageSectionCard
          title="Respostas do questionário"
          description={
            'O que este lead respondeu nos questionários enviados. Atualiza em tempo ' +
            'real conforme ele preenche — Karla acompanha o auto-save; ao submeter, ' +
            'as respostas finais substituem o rascunho.'
          }
        >
          <LeadFormSubmissionsSection leadId={l.id} />
        </PageSectionCard>

        <PageSectionCard
          title="Propostas"
          description={
            isCustomer
              ? 'Histórico de propostas. A proposta aceita virou contrato assinado — cards consultáveis.'
              : 'Rascunhos e propostas enviadas para este lead. Criar um rascunho não envia ' +
                'automaticamente — Karla revisa antes de disparar o link público.'
          }
        >
          <LeadProposalsSection
            leadId={l.id}
            leadName={l.full_name}
            leadClinicNameGuess={l.clinic_name_guess}
            leadStage={l.stage}
          />
        </PageSectionCard>

        {/* Ações disponíveis — em estado terminal (cliente), o leque
            simplifica drasticamente. Helper `hasStageAction` decide o
            que aparece sem condicionais espalhadas. PR-M1-01c-iii.b. */}
        <PageSectionCard
          title="Ações disponíveis"
          description={
            isCustomer
              ? 'Lead virou cliente. Ações de funil (criar proposta, reenviar questionário) não fazem mais sentido aqui.'
              : 'Acionamentos para este lead no estado atual.'
          }
        >
          <div className="flex flex-wrap gap-2">
            {hasStageAction(l.stage, 'send_form') ? <SendQuestionnaireButton lead={l} /> : null}
            {hasStageAction(l.stage, 'create_proposal') ? (
              <NewProposalDialog leadId={l.id} leadName={l.full_name} />
            ) : null}
            {hasStageAction(l.stage, 'mark_lost') && !isArchived ? (
              <Button size="lg" variant="outline" onClick={() => setLostOpen(true)}>
                Marcar como perdido
              </Button>
            ) : null}
            {hasStageAction(l.stage, 'delete_lead') ? (
              <Button
                size="lg"
                variant="outline"
                onClick={() => setDeleteOpen(true)}
                className="text-destructive hover:text-destructive"
              >
                Excluir lead
              </Button>
            ) : null}
          </div>
          <p className="mt-3 text-xs text-muted-foreground">
            {isCustomer
              ? 'Painel da clínica e download do contrato certificado entram nos próximos PRs do M1 (01f.b + 01g.a).'
              : 'Envio do contrato e provisionamento da clínica entram nos próximos PRs do M1. A timeline abaixo registra automaticamente cada etapa do funil.'}
          </p>
        </PageSectionCard>

        <PageSectionCard
          title="Timeline"
          description={
            activities.data?.length
              ? `${activities.data.length} evento${activities.data.length === 1 ? '' : 's'} registrado${
                  activities.data.length === 1 ? '' : 's'
                }.`
              : 'Nenhum evento ainda.'
          }
        >
          {activities.isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : activities.data?.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Eventos aparecem aqui quando questionário for enviado, proposta for criada, etc. O
              audit trigger também registra mudanças de stage automaticamente.
            </p>
          ) : (
            <ol className="relative space-y-4 border-l border-border pl-4">
              {activities.data?.map((a) => (
                <li key={a.id} className="relative">
                  <span className="absolute top-1 left-[-21px] size-3 rounded-full border-2 border-card bg-accent" />
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <p className="text-sm font-medium text-foreground">
                      {ACTIVITY_LABELS[a.activity_type] ?? a.activity_type}
                    </p>
                    <time className="text-xs text-muted-foreground">
                      {new Date(a.created_at).toLocaleString('pt-BR', {
                        dateStyle: 'short',
                        timeStyle: 'short',
                      })}
                    </time>
                  </div>
                  {a.description ? (
                    <p className="mt-0.5 text-sm text-muted-foreground">{a.description}</p>
                  ) : null}
                </li>
              ))}
            </ol>
          )}
        </PageSectionCard>

        <PageSectionCard
          title="Notas internas"
          description="Apenas visível para o time. Útil pra contexto de conversas, indicações, etc."
        >
          <div className="space-y-3">
            <div className="space-y-2">
              <Label htmlFor="lead-notes">Notas</Label>
              <textarea
                id="lead-notes"
                value={notes}
                onChange={(e) => {
                  setNotes(e.target.value);
                  setNotesDirty(e.target.value !== (l.notes ?? ''));
                }}
                rows={4}
                maxLength={2_000}
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 md:text-sm"
                placeholder="Contexto adicional…"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                size="lg"
                disabled={!notesDirty || updateNotes.isPending}
                onClick={() => {
                  setNotes(l.notes ?? '');
                  setNotesDirty(false);
                }}
              >
                Descartar
              </Button>
              <Button
                size="lg"
                disabled={!notesDirty || updateNotes.isPending}
                onClick={onSaveNotes}
              >
                {updateNotes.isPending ? 'Salvando…' : 'Salvar'}
              </Button>
            </div>
          </div>
        </PageSectionCard>

        <MarkAsLostDialog
          leadId={l.id}
          leadName={l.full_name}
          open={lostOpen}
          onOpenChange={setLostOpen}
        />

        <EditLeadDialog lead={l} open={editOpen} onOpenChange={setEditOpen} />

        <DeleteLeadDialog
          leadId={l.id}
          leadName={l.full_name}
          open={deleteOpen}
          onOpenChange={setDeleteOpen}
          onDeleted={() => navigate('/superadmin/leads', { replace: true })}
        />
      </PageContainer>
    </SuperadminShell>
  );
}

// ---------------------------------------------------------------------
// Helpers locais — pequenos demais pra arquivo próprio, muito específicos
// pra irem em `features/leads/formatters.ts`.
// ---------------------------------------------------------------------

function LeadAddressField({
  label,
  value,
  wide = false,
}: {
  label: string;
  value: string | null;
  wide?: boolean;
}) {
  return (
    <div className={wide ? 'sm:col-span-2' : undefined}>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="font-medium">{value ?? '—'}</dd>
    </div>
  );
}

function formatAddressField(
  address: LeadAddress | null | undefined,
  key: keyof LeadAddress,
): string | null {
  const v = address?.[key];
  return typeof v === 'string' && v.trim().length > 0 ? v : null;
}

function formatStreetLine(address: LeadAddress | null | undefined): string | null {
  if (!address) return null;
  const parts = [address.street, address.number].filter(
    (p): p is string => typeof p === 'string' && p.trim().length > 0,
  );
  return parts.length > 0 ? parts.join(', ') : null;
}

function formatCityUf(address: LeadAddress | null | undefined): string | null {
  if (!address) return null;
  if (address.city && address.uf) return `${address.city} / ${address.uf.toUpperCase()}`;
  if (address.city) return address.city;
  if (address.uf) return address.uf.toUpperCase();
  return null;
}

// Sanity check compile-time — garante que todo tipo de activity tem label.
// Se adicionarem um tipo novo em LEAD_ACTIVITY_TYPES sem mapear aqui, TS
// não reclama porque é array e map — mas o fallback (`?? a.activity_type`)
// evita crash em runtime.
const _check: readonly LeadActivityType[] = LEAD_ACTIVITY_TYPES;
void _check;
